import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  TextField,
  Grid,
  Card,
  CardMedia,
  useTheme,
  useMediaQuery,
  Divider,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';

const ProductDetailModal = ({ open, onClose, product, onContinueShopping }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [hasAddedToCart, setHasAddedToCart] = useState(false);
  
  const { addToCart, getItemQuantity } = useCart();

  // Réinitialiser l'état quand le modal s'ouvre/ferme
  React.useEffect(() => {
    if (open) {
      setHasAddedToCart(false);
      setQuantity(1);
      setCurrentImageIndex(0);
      setShowSuccessAlert(false);
    }
  }, [open]);

  if (!product) return null;

  // Gérer les images du produit
  const productImages = product.imageUrls && product.imageUrls.length > 0 
    ? product.imageUrls 
    : ['https://via.placeholder.com/400x300?text=Produit+Agricole'];

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= (product.quantity || 999)) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      title: product.title,
      price: product.price,
      image: productImages[0],
      farmerName: product.farmerName,
      location: product.location,
      unit: product.unit || 'kg'
    };
    
    addToCart(cartItem, quantity);
    setHasAddedToCart(true);
    setShowSuccessAlert(true);
    
    // Masquer l'alerte après 3 secondes
    setTimeout(() => {
      setShowSuccessAlert(false);
    }, 3000);
  };

  const handlePayOrder = () => {
    // Logique pour procéder au paiement
    console.log('Procéder au paiement');
    // Ici vous pouvez rediriger vers une page de checkout
    onClose();
  };

  const handleContinueShoppingFromModal = () => {
    setHasAddedToCart(false);
    onContinueShopping();
  };

  const handleImageNavigation = (direction) => {
    if (direction === 'next') {
      setCurrentImageIndex((prev) => 
        prev === productImages.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) => 
        prev === 0 ? productImages.length - 1 : prev - 1
      );
    }
  };

  const currentQuantityInCart = getItemQuantity(product.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          m: isMobile ? 0 : 2
        }
      }}
    >
      {/* Header avec titre et bouton fermer */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant={isMobile ? 'h6' : 'h5'} component="h2" noWrap>
          Détails du produit
        </Typography>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Alerte de succès */}
        {showSuccessAlert && (
          <Alert 
            severity="success" 
            sx={{ m: 2, mb: 0 }}
            onClose={() => setShowSuccessAlert(false)}
          >
            Produit ajouté au panier avec succès !
          </Alert>
        )}

        <Box sx={{ p: isMobile ? 1 : 2 }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Section Image */}
            <Grid item xs={12} md={6}>
              <Card sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height={isMobile ? "250" : "350"}
                  image={productImages[currentImageIndex]}
                  alt={product.title}
                  sx={{ objectFit: 'cover' }}
                />
                
                {/* Navigation des images */}
                {productImages.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => handleImageNavigation('prev')}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleImageNavigation('next')}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                    
                    {/* Indicateurs de pagination */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 0.5
                      }}
                    >
                      {productImages.map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                            cursor: 'pointer'
                          }}
                          onClick={() => setCurrentImageIndex(index)}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Card>
            </Grid>

            {/* Section Détails */}
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Titre et prix */}
                <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom fontWeight="bold">
                  {product.title}
                </Typography>
                
                <Typography variant={isMobile ? 'h6' : 'h5'} color="primary" gutterBottom>
                  {product.price?.toLocaleString()} FCFA / {product.unit || 'kg'}
                </Typography>

                {/* Informations du producteur */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Producteur: {product.farmerName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {product.location}
                    </Typography>
                  </Box>
                  
                  {product.farmerPhone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {product.farmerPhone}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Description */}
                {product.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.description}
                    </Typography>
                  </Box>
                )}

                {/* Quantité disponible */}
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={`${product.quantity || 0} ${product.unit || 'kg'} disponible(s)`}
                    color={product.quantity > 10 ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Sélecteur de quantité */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Quantité
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton 
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      size={isMobile ? 'large' : 'medium'}
                    >
                      <RemoveIcon />
                    </IconButton>
                    
                    <TextField
                      value={quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        handleQuantityChange(value);
                      }}
                      type="number"
                      inputProps={{ 
                        min: 1, 
                        max: product.quantity || 999,
                        style: { textAlign: 'center' }
                      }}
                      sx={{ width: 80 }}
                      size="small"
                    />
                    
                    <IconButton 
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= (product.quantity || 999)}
                      size={isMobile ? 'large' : 'medium'}
                    >
                      <AddIcon />
                    </IconButton>
                    
                    <Typography variant="body2" color="text.secondary">
                      {product.unit || 'kg'}
                    </Typography>
                  </Box>
                </Box>

                {/* Prix total */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    Total: {(product.price * quantity).toLocaleString()} FCFA
                  </Typography>
                  {currentQuantityInCart > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Déjà {currentQuantityInCart} {product.unit || 'kg'} dans le panier
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions 
        sx={{ 
          p: isMobile ? 2 : 3, 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 2,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        {!hasAddedToCart ? (
          // Avant ajout au panier - un seul bouton
          <Button
            onClick={handleAddToCart}
            variant="contained"
            startIcon={<CartIcon />}
            fullWidth
            size={isMobile ? 'large' : 'medium'}
            disabled={!product.quantity || product.quantity === 0}
          >
            Ajouter au panier
          </Button>
        ) : (
          // Après ajout au panier - deux boutons
          <>
            <Button
              onClick={handleContinueShoppingFromModal}
              variant="outlined"
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
            >
              Poursuivre mes achats
            </Button>
            
            <Button
              onClick={handlePayOrder}
              variant="contained"
              startIcon={<PaymentIcon />}
              fullWidth={isMobile}
              size={isMobile ? 'large' : 'medium'}
              color="success"
            >
              Payer la commande
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailModal;